import React, { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import Dashboard from '../components/Dashboard';
import TaxCalculation from '../components/TaxCalculation';
import Extraction from '../components/Extraction';
import RulesList from '../components/RulesList';
import FigmaQuestionnairePage from '../pages/QuestionnairePage';
import FlowScreensIndexPage from '../pages/FlowScreensIndexPage';
import CalculationRecordsPage from '../pages/CalculationRecordsPage';
import NewQuestionnairePage from '../pages/NewQuestionnairePage';
import { useAuth } from '../contexts/AuthContext';
import SettingsPage from '../pages/SettingsPage';
import EntryPage from '../pages/EntryPage';
import AdminReviewPage from '../pages/AdminReviewPage';
import AdminGatePage from '../pages/AdminGatePage';
import SignInPage from '../pages/SignInPage';
import RegisterPage from '../pages/RegisterPage';
import ClaimAccountPage from '../pages/ClaimAccountPage';
import ForgotPasswordPage from '../pages/ForgotPasswordPage';
import ResetPasswordPage from '../pages/ResetPasswordPage';
import VerifyEmailPage from '../pages/VerifyEmailPage';
import ProtectedRoute from './ProtectedRoute';
import { getDefaultPublicRedirectPath } from '../shared/routeAccess';
import type { FormData } from '../types/questionnaire';

const DevRoutesLazy =
  process.env.REACT_APP_DEV_TOOLS_ENABLED === 'true'
    ? lazy(() => import('../pages/dev/DevRoutes'))
    : null;

export interface AppRoutesProps {
  savedData: FormData | null;
  onStartNew: () => void;
  onImportData: (data: FormData) => void;
}

// Remount on user change so in-memory form state cannot leak between accounts.
const ClientQuestionnairePage: React.FC = () => {
  const { userId } = useAuth();
  return <NewQuestionnairePage key={userId ?? 'auth-loading'} identified />;
};

const AppRoutes: React.FC<AppRoutesProps> = ({
  savedData,
  onStartNew,
  onImportData,
}) => {
  const publicRedirect = getDefaultPublicRedirectPath();

  const internal = (element: React.ReactElement) => (
    <ProtectedRoute guard="internal" redirectTo={publicRedirect}>
      {element}
    </ProtectedRoute>
  );

  const authGuarded = (element: React.ReactElement) => (
    <ProtectedRoute guard="auth" redirectTo="/signin">
      {element}
    </ProtectedRoute>
  );

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/client" replace />} />
      <Route
        path="/dashboard"
        element={internal(
          <Dashboard
            data={savedData}
            onStartNew={onStartNew}
            onImportData={onImportData}
          />,
        )}
      />
      <Route
        path="/tax-calculation"
        element={internal(<TaxCalculation formData={savedData} />)}
      />
      <Route path="/extraction" element={internal(<Extraction />)} />
      <Route path="/rules" element={internal(<RulesList />)} />
      <Route path="/questionnaire" element={internal(<FigmaQuestionnairePage />)} />
      <Route path="/screens" element={internal(<FlowScreensIndexPage />)} />
      <Route path="/calculation-records" element={internal(<CalculationRecordsPage />)} />

      <Route path="/entry" element={<EntryPage />} />
      <Route path="/new" element={<NewQuestionnairePage />} />
      <Route path="/client" element={authGuarded(<ClientQuestionnairePage />)} />
      <Route path="/settings" element={authGuarded(<SettingsPage />)} />

      <Route
        path="/admin/review"
        element={
          <ProtectedRoute guard="admin" fallback={<AdminGatePage />}>
            <AdminReviewPage />
          </ProtectedRoute>
        }
      />

      <Route path="/signin" element={<SignInPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/claim-account" element={<ClaimAccountPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />

      {DevRoutesLazy ? (
        <Route
          path="/dev/*"
          element={
            <Suspense fallback={null}>
              <DevRoutesLazy />
            </Suspense>
          }
        />
      ) : null}

      <Route path="*" element={<Navigate to={publicRedirect} replace />} />
    </Routes>
  );
};

export default AppRoutes;
